import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { CreditCard, Loader2 } from 'lucide-react';

import { Button } from '@sandtech-hub-monorepo/ui-kit';
import { CreateStripeCustomerPortalSessionDocument } from '@sandtech-hub-monorepo/ehub-client';

import { AppRoutesPaths } from '../../routes/types';
import { useStripeSubscriptions } from '../../data/useStripeSubscriptions';
import { SubscriptionCard } from './components/SubscriptionCard';

/**
 * Card subscriptions: a recurring Stripe charge that unlocks paid modules
 * across whichever programs its plan covers.
 *
 * The sibling Payments tab is a different thing entirely — the degree semester
 * payment plan, an instalment schedule against tuition — but the two are no
 * longer shown to the same learner. Settings picks the tabs by learner path,
 * so a GSA learner never sees Payments and has nothing to distinguish this
 * from, which is why the copy below no longer contrasts them. Only a learner
 * holding both kinds of enrollment sees both tabs.
 *
 * Read-only by design. Everything that changes a subscription — card, invoices,
 * cancellation — lives in Stripe's hosted portal, so there is one place where
 * billing is edited and no second implementation of it to keep correct.
 */
export const SubscriptionTab = () => {
  const { subscriptions, loading, error } = useStripeSubscriptions();
  /*
   * Stripe's hosted portal is where payment methods, invoices and cancellation
   * live; we host none of it. The session is minted per click on purpose — a
   * portal session is short-lived, so one made at page load would be stale by
   * the time anyone pressed anything.
   *
   * The return address is origin-pinned server-side: https, and on the
   * gateway's own origin, because the portal's "return to site" control
   * carries the credibility of arriving from a genuine Stripe page. The
   * deployed site shares that origin, so this works there and is refused on a
   * localhost dev server.
   */
  const [openPortal, { loading: portalLoading, error: portalError }] =
    useMutation(CreateStripeCustomerPortalSessionDocument, {
      onCompleted: (data) => {
        // A full-page navigation, not a new tab: the portal is somewhere the
        // learner goes and comes back from, and its return control expects to
        // own the window it was opened in.
        window.location.assign(data.createStripeCustomerPortalSession.url);
      },
      // The page renders `error`; this only stops Apollo rejecting unhandled.
      onError: () => undefined,
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading your subscription…
      </div>
    );
  }

  if (error) {
    return (
      <p className="py-16 text-center text-muted-foreground">{error.message}</p>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex max-w-md flex-col items-center gap-4 p-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            <CreditCard className="h-8 w-8 text-muted-foreground" />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-lg font-semibold">No subscription</p>
            <p className="text-sm text-muted-foreground">
              You don&apos;t have an All-Access subscription. Free modules stay
              open without one.
            </p>
          </div>
          <Button asChild className="rounded-full px-8">
            <Link to={AppRoutesPaths.Subscribe}>See the plans</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">
        An All-Access subscription unlocks the paid modules of every program its
        plan covers.
      </p>

      <div className="flex flex-col gap-4">
        {subscriptions.map((subscription) => (
          <SubscriptionCard key={subscription.id} subscription={subscription} />
        ))}
      </div>

      <div className="flex flex-col gap-2">
        <Button
          variant="outline"
          disabled={portalLoading}
          onClick={() =>
            openPortal({
              variables: { input: { returnUrl: window.location.href } },
            })
          }
          className="w-full rounded-full border-primary text-primary hover:bg-primary/10 hover:text-primary sm:w-auto sm:self-start sm:px-8"
        >
          {portalLoading ? 'Opening Stripe…' : 'Manage my subscription'}
        </Button>
        <p className="text-sm text-muted-foreground">
          Change your card, read past invoices or cancel, all on Stripe.
        </p>
        {/*
          The server pins the return address to its own https origin, so this
          is refused on a localhost dev server and says so. Its message is
          written for a learner, so it is shown as-is.
        */}
        {portalError && (
          <p className="text-sm text-destructive">{portalError.message}</p>
        )}
      </div>
    </div>
  );
};
